// src/components/ViewServiceButton.js
import React from 'react';
import PropTypes from 'prop-types'; // Import PropTypes for type checking
import { FaEye } from 'react-icons/fa'; // Import the specific icon needed

/**
 * A reusable button component specifically for triggering a 'View Details' action.
 * It handles its own hover effects based on styles passed via props.
 *
 * @param {object} props - The component props.
 * @param {function} props.onClick - The function to execute when the button is clicked.
 * @param {object} props.baseStyle - The style object for the button's default state. Must include backgroundColor.
 * @param {object} props.hoverStyle - The style object containing styles for the hover state (e.g., { backgroundColor: '#newcolor' }). Must include backgroundColor.
 */
const ViewServiceButton = ({ onClick, baseStyle, hoverStyle }) => {

    // Basic validation to ensure necessary style properties are present
    // This prevents errors if the parent component forgets to pass valid styles.
    const safeBaseBgColor = baseStyle?.backgroundColor || '#ecf0f1'; // Provide a fallback default
    const safeHoverBgColor = hoverStyle?.backgroundColor || '#dfe4e7'; // Provide a fallback default

    if (!baseStyle || !hoverStyle) {
        console.warn("ViewServiceButton: baseStyle or hoverStyle prop is missing. Using default fallbacks.");
    } else if (!baseStyle.backgroundColor || !hoverStyle.backgroundColor) {
        console.warn("ViewServiceButton: backgroundColor is missing in baseStyle or hoverStyle. Using default fallbacks.");
    }

    // Combine base styles with any potentially missing defaults to ensure the button always renders
    const combinedBaseStyle = {
        flex: 1, // Example default layout properties, adjust if needed
        border: '1px solid #bdc3c7',
        borderRadius: '6px',
        padding: '0.7rem',
        cursor: 'pointer',
        fontSize: '0.9rem',
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.4rem',
        transition: 'background-color 0.2s ease',
        ...baseStyle, // Spread passed baseStyle, overriding defaults
        backgroundColor: safeBaseBgColor, // Ensure background color is set
    };

    return (
        <button
            style={combinedBaseStyle} // Apply the combined base styles
            onClick={onClick} // Attach the click handler passed via props
            onMouseEnter={(e) => {
                // Apply hover background color on mouse enter
                e.currentTarget.style.backgroundColor = safeHoverBgColor;
            }}
            onMouseLeave={(e) => {
                // Revert to base background color on mouse leave
                e.currentTarget.style.backgroundColor = safeBaseBgColor;
            }}
            title="View Service Details" // Enhance accessibility
        >
            {/* Icon with a small gap */}
            <FaEye style={{ marginRight: '0.4rem' }} />
            {/* Button text */}
            View
        </button>
    );
};

// Define PropTypes for the component for better development experience
ViewServiceButton.propTypes = {
    /** Function to call when the button is clicked */
    onClick: PropTypes.func.isRequired,
    /** Style object for the button's normal state (must include backgroundColor) */
    baseStyle: PropTypes.object.isRequired,
    /** Style object for the button's hover state (must include backgroundColor) */
    hoverStyle: PropTypes.object.isRequired,
};

export default ViewServiceButton;